import { cn } from "@/lib/utils";
import { Crown, Lock } from "lucide-react";
import { useState } from "react";

interface ElementGridItemProps {
  title: string;
  imageSrc: string;
  isPremium?: boolean;
  onClick: () => void;
  showTitleAlways?: boolean;
}

export function ElementGridItem({ 
  title, 
  imageSrc, 
  isPremium, 
  onClick,
  showTitleAlways = false
}: ElementGridItemProps) {
  const [isLoading, setIsLoading] = useState(true);
  
  // Generate an optimized thumbnail URL using Supabase's built-in image transformations
  const thumbnailSrc = `${imageSrc.split('?')[0]}?width=50&quality=30&format=webp`;

  return (
    <div className="flex flex-col gap-2">
      <button
        onClick={onClick}
        className={cn(
          "group relative aspect-square w-full overflow-hidden rounded-md",
          "border border-border/50 bg-background/95 shadow-sm",
          "transition-all duration-200 hover:shadow-md",
          "focus:outline-none focus:ring-2 focus:ring-blue-500",
          isPremium && "cursor-pointer hover:ring-purple-500/50"
        )}
      >
        {/* Single image with proper loading strategy */}
        <img
          src={imageSrc}
          alt={title}
          className={cn(
            "absolute inset-0 h-full w-full object-cover",
            "transition-opacity duration-300",
            isLoading ? "opacity-0" : "opacity-100",
            isPremium && "group-hover:opacity-40"
          )}
          loading="lazy"
          onLoad={() => setIsLoading(false)}
        />
        
        {/* Low quality placeholder */}
        {isLoading && (
          <img
            src={thumbnailSrc}
            alt=""
            className="absolute inset-0 h-full w-full object-cover blur-sm scale-110"
            aria-hidden="true"
          />
        )}
        
        {/* Premium overlay */}
        {isPremium && (
          <div className={cn(
            "absolute inset-0 flex flex-col items-center justify-center gap-2",
            "bg-purple-500/0 transition-all duration-200",
            "group-hover:bg-purple-500/10",
            "opacity-0 group-hover:opacity-100"
          )}>
            <Lock className="h-6 w-6 text-purple-600" strokeWidth={1.5} />
            <span className="text-xs font-medium text-purple-700">
              Login Required
            </span>
          </div>
        )}
        
        {/* Keep the hover overlay for additional details */}
        {!showTitleAlways && (
          <div className={cn(
            "absolute inset-0 flex items-end justify-between p-1.5",
            "bg-gradient-to-t from-black/50 to-transparent",
            "transition-opacity duration-200",
            isLoading ? "opacity-0" : "opacity-0 group-hover:opacity-100"
          )}>
            {isPremium && (
              <Crown className="h-3 w-3 text-yellow-400" />
            )}
          </div>
        )}
      </button>
      
      {/* Title below the image */}
      {showTitleAlways && (
        <div className="text-xs font-medium text-gray-800 truncate px-1">
          {title}
          {isPremium && (
            <Crown className="h-3 w-3 text-yellow-400 inline-block ml-1" />
          )}
        </div>
      )}
    </div>
  );
}
